/* eslint-disable @typescript-eslint/no-use-before-define */
import React, {useContext, FC, useEffect, useCallback} from 'react';
import {Navigation} from 'react-minimal-side-navigation';
import 'react-minimal-side-navigation/lib/ReactMinimalSideNavigation.css';
import {GrContact} from "@react-icons/all-files/gr/GrContact";
import {oxygenTtamayoContext} from '../../context';
import {flattenNavigationItemsFromLevel, removeObjectsByKeyValues, categoryUrlHasChildren, getPluralName, replaceKeysNested, nestObjInSelectedKey, alterObjProperty, findByProperty, nestedObjectToArray} from '../../utils';
import LogoSVG from "../../../../img/logo-ttamayo-vertical.svg?component";

const pluralNamesNavigationItems = {
  "Blog": "Ver todos los artículos",
  "Cursos y clases": "Ver todos los cursos y clases",
  "Quiénes somos": "Acerca de nosotros",
  "Productos": "Ver todos los productos",
}

const menuItemsKeys = {nameKey: "name", childrenKey: "children", urlKey: "url"};

type MobileSidebarProps = {
  sidebarIsOpen: boolean,
  toggleSidebar: () => void,
  isMobile: boolean,
  isTablet: boolean,
}

// Componente para el ícono de contacto
const ContactIcon = () => {
  return <GrContact />;
};

export const MobileSidebar: FC<MobileSidebarProps> = (props) => {
  const {main_menu, category_list} = useContext(oxygenTtamayoContext);
  const mainMenu = nestedObjectToArray(main_menu, "children");

  // Función para cerrar el sidebar (memoizada para evitar re-renders)
  const handleCloseSidebar = useCallback(() => {
    if (props.sidebarIsOpen) {
      props.toggleSidebar();
    }
  }, [props.sidebarIsOpen, props.toggleSidebar]);

  // Manejar el escape key para cerrar el sidebar
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && props.sidebarIsOpen) {
        handleCloseSidebar();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [props.sidebarIsOpen, handleCloseSidebar]);

  // Agregar/quitar la clase "overflow-hidden" al body para prevenir scroll cuando el sidebar está abierto
  useEffect(() => {
    if (props.sidebarIsOpen) {
      document.body.classList.add("overflow-hidden");
      document.documentElement.classList.add("overflow-hidden");
      document.documentElement.classList.add("sidebar-visible");
      // Bajar el z-index del sidebar de cursos cuando el sidebar móvil está abierto
      const cursosSidebar = document.getElementById('tt-cursos-sidebar');
      const parentElement = cursosSidebar?.closest('.ct-div-block');

      if (cursosSidebar) {
        (cursosSidebar as HTMLElement).style.zIndex = '1';
      }

      if (parentElement) {
        (parentElement as HTMLElement).style.zIndex = '1';
      }
    } else {
      document.body.classList.remove("overflow-hidden");
      document.documentElement.classList.remove("overflow-hidden");
      document.documentElement.classList.remove("sidebar-visible");

      // Restaurar el z-index original del sidebar de cursos
      const cursosSidebar = document.getElementById('tt-cursos-sidebar');
      const parentElement = cursosSidebar?.closest('.ct-div-block');

      if (cursosSidebar) {
        (cursosSidebar as HTMLElement).style.zIndex = '';
      }

      if (parentElement) {
        (parentElement as HTMLElement).style.zIndex = '';
      }
    }
    // Cleanup
    return () => {
      document.body.classList.remove("overflow-hidden");
      document.documentElement.classList.remove("overflow-hidden");
      document.documentElement.classList.remove("sidebar-visible");

      // Asegurar que se restaure el z-index al desmontar el componente
      const cursosSidebar = document.getElementById('tt-cursos-sidebar');
      const parentElement = cursosSidebar?.closest('.ct-div-block');

      if (cursosSidebar) {
        (cursosSidebar as HTMLElement).style.zIndex = '';
      }

      if (parentElement) {
        (parentElement as HTMLElement).style.zIndex = '';
      }
    };
  }, [props.sidebarIsOpen]);

  interface NavigationItem {
    title: string;
    itemId: string;
    subNav: NavigationItem[];
  }

  // Renderiza los elementos de navegación
  const renderNavigationItems = (items: any[], {nameKey, childrenKey, urlKey}: { nameKey: string; childrenKey: string; urlKey: string; }): NavigationItem[] => {
    return items.map(i => {
      const childLength = i[childrenKey].length;
      return {
        title: i[nameKey],
        itemId: childLength ? "%" + i[urlKey] : i[urlKey],
        subNav: childLength ? [{
          title: getPluralName(i[nameKey], "Ver todos", pluralNamesNavigationItems),
          itemId: i[urlKey],
          subNav: []
        }, ...renderNavigationItems(i[childrenKey], {nameKey, childrenKey, urlKey})] : []
      }
    });
  }

  // Procesar los datos del menú
  const menuItemsUnfiltered =
    flattenNavigationItemsFromLevel(
      nestedObjectToArray(nestObjInSelectedKey(
        replaceKeysNested(main_menu, {
          title: "name",
        }),
        {
          keyMatch: "name",
          valueMatch: "Blog"
        }, {
          keyToInsert: "children",
          objToInsert: nestedObjectToArray(replaceKeysNested(category_list, {
            child_categories: "children",
            category_url: "url"
          }), "child_categories")
        }
      ), "children"), 1, menuItemsKeys
    );

  const menuItemsUnNestedReplacement = removeObjectsByKeyValues(menuItemsUnfiltered, "children", [{"name": "Sin categoría"}]);

  const menuItems = alterObjProperty(menuItemsUnNestedReplacement, "children", {
    valueCondition: (item: any) => item.depth > 1
  }, {
    keyToAlter: "name",
    alterFunction: (item: any) => "— ".repeat(item.depth - 1) + item.name
  });

  // Maneja la navegación cuando se selecciona un item
  const handleNavigationSelect = (e: any) => {
    if (
      !e.itemId.startsWith('%')
      && !categoryUrlHasChildren(flattenNavigationItemsFromLevel(menuItems, 0, menuItemsKeys), e.itemId, menuItemsKeys)
    ) {
      // Cerramos el sidebar antes de navegar
      props.toggleSidebar();
      // Añadimos un pequeño retraso para la navegación para que se vea el cierre
      setTimeout(() => {
        window.location.href = e.itemId;
      }, 300);
    }
  }

  const getUbicacionYConctacto = findByProperty(mainMenu, i => i.title === "Ubicación y contacto");

  return (
    <>
      {/* Overlay con efecto blur para cerrar el sidebar */}
      <div
        className={`fixed inset-0 bg-black/50 transition-all duration-300 ease-out cursor-pointer z-8000 ${props.sidebarIsOpen ? 'opacity-100 visible backdrop-blur-sm' : 'opacity-0 invisible backdrop-blur-none'}`}
        aria-hidden={!props.sidebarIsOpen}
        onClick={handleCloseSidebar}
        role="button"
        tabIndex={0}
        style={{
          willChange: 'opacity, backdrop-filter',
          backdropFilter: props.sidebarIsOpen ? 'blur(8px)' : 'blur(0px)'
        }}
      />
      {/* Contenido del sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-white z-9000 transition-transform duration-300 ease-out overflow-y-auto border-r-2 border-black border-opacity-10 p-4 shadow-md ${props.sidebarIsOpen ? 'translate-x-0 visible' : '-translate-x-full invisible'}`}
        aria-hidden={!props.sidebarIsOpen}
        onClick={(e) => e.stopPropagation()} // Evitar que clicks en el sidebar se propaguen al overlay
      >
        {/* Botón para cerrar en la esquina superior derecha */}
        <button
          className="absolute top-4 right-4 bg-transparent border-0 cursor-pointer text-gray-600 hover:text-gray-800 transition-colors duration-200"
          aria-label="Close sidebar menu"
          onClick={handleCloseSidebar}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="flex items-center justify-center mt-2 text-center py-6">
          <span>
            <a target="_self" href="/">
              <LogoSVG className="w-20" />
            </a>
          </span>
        </div>

        <Navigation
          activeItemId={window.location.href}
          items={renderNavigationItems(menuItems, menuItemsKeys)}
          onSelect={(e) => handleNavigationSelect(e)}
        />

        <div className="bottom-0 w-full mt-8 pb-8 bg-white">
          <Navigation
            activeItemId={window.location.href}
            items={[
              {
                title: "Contáctanos",
                itemId: getUbicacionYConctacto.url,
                elemBefore: ContactIcon
              }
            ]}
            onSelect={(e) => handleNavigationSelect(e)}
          />
        </div>
      </div>
    </>
  );
}
